import { Grid } from '@backstage/ui';
import {
  EntityApiDefinitionCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  EntityRelationWarning,
  isKind,
  hasCatalogProcessingErrors,
  hasRelationWarnings,
  isOrphan,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import {
  EntityTechdocsContent,
  isTechDocsAvailable,
} from '@backstage/plugin-techdocs';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  Entity,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

/** PS-provisioned labs (provision.yml or image-build) expose infrastructure.backstage.io/type. */
const isPsInfrastructureEntity = (entity: Entity) =>
  !!entity.metadata.annotations?.['infrastructure.backstage.io/type'];

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  ReportIssue,
  TextSize,
  LightBox,
} from '@backstage/plugin-techdocs-module-addons-contrib';

import { AwsInfrastructureInfoCard } from './AwsInfrastructureInfoCard';
import { AzureInfrastructureInfoCard } from './AzureInfrastructureInfoCard';
import { GcpInfrastructureInfoCard } from './GcpInfrastructureInfoCard';
import { ElastiCacheInfoCard } from './ElastiCacheInfoCard';
import { RedisEnterpriseInfoCard } from './RedisEnterpriseInfoCard';
import { CRDBInfoCard } from './CRDBInfoCard';
import { RedisCloudVPCPeeringInfoCard } from './RedisCloudVPCPeeringInfoCard';
import { RedisCloudPSCInfoCard } from './RedisCloudPSCInfoCard';
import { TransitGatewayInfoCard } from './TransitGatewayInfoCard';
import { PrometheusGrafanaInfoCard } from './PrometheusGrafanaInfoCard';
import { RedisOSSInfoCard } from './RedisOSSInfoCard';
import { RedisOSSSentinelInfoCard } from './RedisOSSSentinelInfoCard';
import { MemtierBenchmarkInfoCard } from './MemtierBenchmarkInfoCard';
import { RIOTXInfoCard } from './RIOTXInfoCard';
import { EKSClusterInfoCard } from './EKSClusterInfoCard';
import { GKEClusterInfoCard } from './GKEClusterInfoCard';
import { OpenShiftInfoCard } from './OpenShiftInfoCard';
import { PostgreSQLCDCInfoCard } from './PostgreSQLCDCInfoCard';
import { RDSPostgresCDCInfoCard } from './RDSPostgresCDCInfoCard';
import { MySQLCDCInfoCard } from './MySQLCDCInfoCard';
import { RDSMySQLCDCInfoCard } from './RDSMySQLCDCInfoCard';
import { MongoDBCDCInfoCard } from './MongoDBCDCInfoCard';
import { OracleCDCInfoCard } from './OracleCDCInfoCard';
import { SQLServerCDCInfoCard } from './SQLServerCDCInfoCard';
import { DB2CDCInfoCard } from './DB2CDCInfoCard';
import { CassandraCDCInfoCard } from './CassandraCDCInfoCard';
import { SambaADInfoCard } from './SambaADInfoCard';
import { RDIServerInfoCard } from './RDIServerInfoCard';
import { SplunkInfoCard } from './SplunkInfoCard';
import { VaultInfoCard } from './VaultInfoCard';
import { GemFireInfoCard } from './GemFireInfoCard';
import { GcpImageBuildInfoCard } from './GcpImageBuildInfoCard';
import { BuildLogsTab } from './BuildLogsTab';
import { DestroyInfrastructureCard } from './DestroyInfrastructureCard';
import { TTLWarningCard } from './TTLWarningCard';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
      <TextSize />
      <LightBox />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid.Item colSpan="12">
          <EntityOrphanWarning />
        </Grid.Item>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid.Item colSpan="12">
          <EntityRelationWarning />
        </Grid.Item>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid.Item colSpan="12">
          <EntityProcessingErrorsPanel />
        </Grid.Item>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

/** Component Overview: About, Links, stack info cards — no relation/graph chrome. */
const componentOverviewContent = (
  <Grid.Root columns="12" gap="4">
    {entityWarningContent}

    <Grid.Item colSpan="12">
      <TTLWarningCard />
    </Grid.Item>

    <Grid.Item colSpan={{ initial: '12', md: '6' }}>
      <EntityAboutCard />
    </Grid.Item>
    <Grid.Item colSpan={{ initial: '12', md: '6' }}>
      <EntityLinksCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <AwsInfrastructureInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <GcpInfrastructureInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <AzureInfrastructureInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <GcpImageBuildInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <PrometheusGrafanaInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <RedisOSSInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <RedisOSSSentinelInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <MemtierBenchmarkInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <RIOTXInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <EKSClusterInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <GKEClusterInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <OpenShiftInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <ElastiCacheInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <RedisEnterpriseInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <GemFireInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <CRDBInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <RedisCloudVPCPeeringInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <RedisCloudPSCInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <TransitGatewayInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <PostgreSQLCDCInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <RDSPostgresCDCInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <RDSMySQLCDCInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <MySQLCDCInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <MongoDBCDCInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <OracleCDCInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <SQLServerCDCInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <DB2CDCInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <CassandraCDCInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <RDIServerInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <SambaADInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <VaultInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <SplunkInfoCard />
    </Grid.Item>

    <Grid.Item colSpan="12">
      <DestroyInfrastructureCard />
    </Grid.Item>
  </Grid.Root>
);

/**
 * All Components share one layout: Overview (About / Links / infra cards),
 * Docs when present, Workflow for provisioned labs. No Relations graph,
 * Has Subcomponents, CI/CD, API, or Dependencies tabs.
 */
const componentPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {componentOverviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={isTechDocsAvailable}>
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/workflow"
      title="Workflow"
      if={isPsInfrastructureEntity}
    >
      <BuildLogsTab />
    </EntityLayout.Route>
  </EntityLayout>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid.Root columns="12" gap="4">
        {entityWarningContent}
        <Grid.Item colSpan={{ initial: '12', md: '6' }}>
          <EntityAboutCard />
        </Grid.Item>
        <Grid.Item colSpan={{ initial: '12', md: '6' }}>
          <EntityCatalogGraphCard height={400} />
        </Grid.Item>
        <Grid.Item colSpan={{ initial: '12', md: '4' }}>
          <EntityLinksCard />
        </Grid.Item>
        <Grid.Item colSpan="12">
          <Grid.Root columns={{ initial: '1', md: '2' }} gap="4">
            <Grid.Item colSpan="1">
              <EntityProvidingComponentsCard />
            </Grid.Item>
            <Grid.Item colSpan="1">
              <EntityConsumingComponentsCard />
            </Grid.Item>
          </Grid.Root>
        </Grid.Item>
      </Grid.Root>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid.Root columns="12" gap="4">
        <Grid.Item colSpan="12">
          <EntityApiDefinitionCard />
        </Grid.Item>
      </Grid.Root>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid.Root columns={{ initial: '1', md: '2' }} gap="4">
        {entityWarningContent}
        <Grid.Item colSpan="1">
          <EntityUserProfileCard />
        </Grid.Item>
        <Grid.Item colSpan="1">
          <EntityOwnershipCard />
        </Grid.Item>
      </Grid.Root>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid.Root columns={{ initial: '1', md: '2' }} gap="4">
        {entityWarningContent}
        <Grid.Item colSpan="1">
          <EntityGroupProfileCard />
        </Grid.Item>
        <Grid.Item colSpan="1">
          <EntityOwnershipCard />
        </Grid.Item>
        <Grid.Item colSpan="1">
          <EntityMembersListCard />
        </Grid.Item>
        <Grid.Item colSpan="1">
          <EntityLinksCard />
        </Grid.Item>
      </Grid.Root>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid.Root columns="12" gap="4">
        {entityWarningContent}
        <Grid.Item colSpan={{ initial: '12', md: '6' }}>
          <EntityAboutCard />
        </Grid.Item>
        <Grid.Item colSpan={{ initial: '12', md: '6' }}>
          <EntityCatalogGraphCard height={400} />
        </Grid.Item>
        <Grid.Item colSpan={{ initial: '12', md: '4' }}>
          <EntityLinksCard />
        </Grid.Item>
        <Grid.Item colSpan={{ initial: '12', md: '8' }}>
          <EntityHasComponentsCard />
        </Grid.Item>
        <Grid.Item colSpan={{ initial: '12', md: '6' }}>
          <EntityHasApisCard />
        </Grid.Item>
        <Grid.Item colSpan={{ initial: '12', md: '6' }}>
          <EntityHasResourcesCard />
        </Grid.Item>
      </Grid.Root>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid.Root columns="12" gap="4">
        {entityWarningContent}
        <Grid.Item colSpan={{ initial: '12', md: '6' }}>
          <EntityAboutCard />
        </Grid.Item>
        <Grid.Item colSpan={{ initial: '12', md: '6' }}>
          <EntityCatalogGraphCard height={400} />
        </Grid.Item>
        <Grid.Item colSpan={{ initial: '12', md: '6' }}>
          <EntityHasSystemsCard />
        </Grid.Item>
      </Grid.Root>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={isTechDocsAvailable}>
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

/** Fallback for Location / Template / other kinds — not the stack Component page. */
const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid.Root columns="12" gap="4">
        {entityWarningContent}
        <Grid.Item colSpan={{ initial: '12', md: '6' }}>
          <EntityAboutCard />
        </Grid.Item>
        <Grid.Item colSpan={{ initial: '12', md: '6' }}>
          <EntityLinksCard />
        </Grid.Item>
      </Grid.Root>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs" if={isTechDocsAvailable}>
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
